import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import ErrorComponent from '@components-clean/core/error';
import { RowMenuAction } from '@components-clean/core/grid/grid.interface';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import Grid from '@components-clean/core/grid-fe/index';
import { FullPageInfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { useAuthorization, useLocalState } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { GET_DQ_REPORTS } from '@queries/data-quality';
import { getDataQualityReports, getDataQualityReportsVariables } from '@queries/generated/data-quality.generated';
import Modules from '@config/modules';
import { SaveFile } from '@apis/data-quality';

interface Props {
    dataQualityReports: DataQualityReport[];
    studyRef: string;
}

interface DataQualityReport {
    name: string;
    extension: string;
}

const Content = ({ dataQualityReports, studyRef }: Props) => {
    const { t } = useTranslation('dataQuality', { keyPrefix: 'dataQualityReports' });
    const user = useAuthorization();

    const { state } = useLocalState();

    const columns: GridColumnProps[] = [{ field: 'name', title: t('gridColName') }];

    const [showDialog, setShowDialog] = React.useState(false);
    const confirmationDialog = () => (
        <ConfirmationDialog
            dataTestId="report-download-dialog"
            title={t('downloadDialogTitle')}
            onClose={() => setShowDialog(false)}
            secondaryBtnLabel="Close"
        >
            {t('downloadDialogContent')}
        </ConfirmationDialog>
    );

    const rowMenuActions: RowMenuAction[] = [
        {
            label: t('gridActionDownloadReport'),
            id: 0,
            onClick: (dataItem): void => {
                const { name, extension } = dataItem as DataQualityReport;

                const fileName = `${name}.${extension}`;

                SaveFile(studyRef, `${fileName}`, user!.token)
                    .then(() => {
                        setShowDialog(true);
                    })
                    .catch((e) => window.console.log(e));
            },
            icon: ['far', 'download'],
        },
    ];
    // eslint-disable-next-line max-len
    const exportFileName = `${t('gridExportFileName')}_${state?.selectedStudy?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-yyyy')}`;

    return (
        <>
            {showDialog && confirmationDialog()}
            <div className="k-h-full">
                <Grid
                    data={dataQualityReports}
                    title={t('gridTitle')}
                    testDataId="data-quality-reports-grid"
                    columns={columns}
                    maxHeight="auto"
                    rowMenuActions={rowMenuActions}
                    exportFilename={exportFileName}
                    showAddIcon={false}
                    gridSettingsMenuDisabled
                    auditDescriptionPrefix="Data Quality"
                    service={AuditServiceType.CENTRAL_MONITORING_2}
                    area={AuditApplicationArea.DATA_QUALITY}
                    dataType={AuditDataType.EXCEL_EXPORT}
                />
            </div>
        </>
    );
};

const DataFetchWrapper = ({ studyRef }: { studyRef: string }) => {
    const { data, loading } = useQuery<getDataQualityReports, getDataQualityReportsVariables>(GET_DQ_REPORTS, {
        variables: { studyRef },
    });

    if (loading) return <Loading />;

    const formGridData = () => {
        return (data?.dataQualityReports || [])
            ?.flatMap<DataQualityReport>((s) => {
                if (!s) return [];

                const sep = s?.lastIndexOf('.');
                if (!sep) return [];

                const fileName = s?.substring(0, sep);
                const fileExt = s?.substring(sep + 1);

                return [{ name: fileName, extension: fileExt }];
            })
            .sort((a: DataQualityReport, b: DataQualityReport) => a.name.localeCompare(b.name));
    };

    return <Content dataQualityReports={formGridData()} studyRef={studyRef} />;
};

const DataQualityPage = () => {
    const { t } = useTranslation('home');

    const user = useAuthorization();

    const { state } = useLocalState();

    if (!state) {
        return <ErrorComponent error={t('unexpectedError')} />;
    }

    // We only care if the org/study has the module and screen permission, not read/write access
    if (user != null && user.hasModule && !user.hasModule(Modules.DataQuality))
        return <FullPageInfoField message={t('pageDataErrors.noScreenPermission')} type="Info" />;

    const study = state.selectedStudy;

    if (!study) {
        return <span>{t('pleaseSelectStudy')}</span>;
    }

    return <DataFetchWrapper studyRef={study.id as string} />;
};

export default DataQualityPage;
